<script lang="ts">
  import { onMount } from "svelte";
  import { fade } from "svelte/transition";

  import closeIcon from "./assets/close.svg?raw";
  import settingsGearIcon from "./assets/settings.svg?raw";

  // @ts-ignore
  import SoundCardTitle from "./SoundCardTitle.svelte";
  import Attribution from "./Attribution.svelte";

  import { soundBoard } from "./store";
  // @ts-ignore
  import { fetchAttribution, unlinkSound } from "./global";
  import { stopPropagation } from "./genericHandlers";
  // @ts-ignore
  import { Howl } from "howler";
  import type { SoundCard } from "./SoundCard";
  import { PlayQueue } from "./lib/playQueue";
  import { Duration, DurationRange } from "./lib/duration";
  import { RepeatSettings } from "./lib/repeat";
  import { WELL_KNOWN_AMBIENCE_DURATIONS } from "./lib/constants";
  import { SoundCache, getOrCreateSoundCache } from "./lib/soundCache";
  import { GuidedSceneSettingIds } from "./GuidedSceneSettings";
    import { htmlDataTypeToHowlerFormat } from "./lib/howler";

  export let scene;
  export let card: SoundCard;
  export let playSettingsChangedCallback;
  export let nameChangeCallback;
  export let lanePlaying = false;
  export let closeable = true;

  let isEnabled = true;
  let configuring = false;

  let audio: Howl | null = null;
  let soundCache: SoundCache = null;

  // Element bindings
  let volume;
  let enabled;
  let fromDurationName: string = "0s";
  let toDurationName: string = "0s";

  let fromDurations: Map<string, Duration> = WELL_KNOWN_AMBIENCE_DURATIONS;
  let toDurations: Map<string, Duration> = WELL_KNOWN_AMBIENCE_DURATIONS;

  let attribution = null;
  let queue = null;

  $: advancedControlsEnabled = scene.settings.getValue(
    GuidedSceneSettingIds.AdvancedAmbienceLoopControls,
  );

  onMount(async () => {
    soundCache = await getOrCreateSoundCache();
    const soundUrl: string = await soundCache.fetchSound(card.sound);
    const shouldUseWebAudio: boolean = await soundCache.shouldUseWebAudio(card.sound);
    audio = new Howl({
      src: [soundUrl],
      // If the sound is in the cache, we use WebAudio, which loops smoothly but requires preloading the sound.
      // Ref: https://stackoverflow.com/a/7520220
      html5: !shouldUseWebAudio,
      loop: false,
      format: [htmlDataTypeToHowlerFormat(card.sound.html_data_type)],
    });
    audio.on("end", () => {
      if (isEnabled) {
        enqueuePlay();
      }
    });
    fetchAttribution(card.sound.id, (att) => (attribution = att)); // Might be null if there is no attribution
    if (card.playSettings.repeat) {
      const currentFromDuration = card.playSettings.repeat.every.from;
      const currentToDuration = card.playSettings.repeat.every.to;
      fromDurations.set(
        currentFromDuration.asDisplayString(),
        currentFromDuration,
      );
      fromDurationName = currentFromDuration.asDisplayString();
      toDurations.set(currentToDuration.asDisplayString(), currentToDuration);
      toDurationName = currentToDuration.asDisplayString();
      queue = PlayQueue.fromSettings(card.playSettings.repeat);
    } else {
      queue = PlayQueue.fromSettings(RepeatSettings.LOOP());
    }
    volume.value = card.playSettings.volume;
    enabled.checked = card.playSettings.enabled;
    isEnabled = card.playSettings.enabled;
    changeVolume(card.playSettings.volume, true);
    if (lanePlaying) {
      enqueuePlay();
    }
  });

  $: if (audio && volume && scene) {
    const curVolume = volume.valueAsNumber;
    changeVolume(curVolume * (scene.globalVolume / 100), false);
  }

  const handleNameChangeRequest = (newName) => {
    if (nameChangeCallback) {
      nameChangeCallback(scene.id, card.id, newName);
    }
  };

  const changeVolume = (newVol, notifyCallback: boolean) => {
    if (audio) {
      audio.volume(newVol / 100);
    }
    if (notifyCallback && playSettingsChangedCallback) {
      playSettingsChangedCallback(scene.id, card.id, (old) => {
        old.volume = newVol;
        return old;
      });
    }
  };

  export function stop() {
    // @ts-ignore
    if (audio) {
      audio.stop();
    }
    if (queue) {
      queue.stop();
    }
  }

  export function enqueuePlay() {
    if (queue.hasNext()) {
      return;
    }
    queue.waitForNext(() => {
      if (audio && lanePlaying && isEnabled) {
        // @ts-ignore
        audio.play();
      }
    });
  }

  export function pause() {
    if (audio && isEnabled) {
      // @ts-ignore
      audio.pause();
    }
  }

  // @ts-ignore
  const handleVolumeChange = (evt) => {
    changeVolume(volume.valueAsNumber, true);
  };

  const handleEnabledChange = () => {
    isEnabled = enabled.checked;
    if (isEnabled && lanePlaying) {
      enqueuePlay();
    } else {
      stop();
    }
    if (playSettingsChangedCallback) {
      playSettingsChangedCallback(scene.id, card.id, (old) => {
        old.enabled = isEnabled;
        return old;
      });
    }
  };
  const deleteCard = () => {
    // @ts-ignore
    stop();
    soundBoard.DeleteCard(scene.id, card["id"]);
    const soundId = card["sound"].id;
    unlinkSound(soundId, $soundBoard);
  };

  function handleClickSettings(evt) {
    configuring = !configuring;
  }

  function handleDurationChange() {
    const newFrom = fromDurations.get(fromDurationName);
    let newTo = toDurations.get(toDurationName);
    if (newFrom.greaterThan(newTo)) {
      newTo = newFrom;
      toDurationName = fromDurationName;
    }

    const newRepeatSettings =
      card.playSettings.repeat !== null
        ? card.playSettings.repeat
        : RepeatSettings.LOOP();
    newRepeatSettings.setEvery((every: DurationRange) => {
      return new DurationRange(newFrom, newTo);
    });
    if (playSettingsChangedCallback) {
      playSettingsChangedCallback(scene.id, card.id, (old) => {
        old.repeat = newRepeatSettings;
        return old;
      });
    }
    if (queue) {
      queue.reconfigure((settings: RepeatSettings): RepeatSettings => {
        return newRepeatSettings;
      });
    }
  }
</script>

<div
  class="soundcard soundcard-ambience-dimensions soundcard-ambience-container editable-icon"
  class:soundcard-off={isEnabled}
>
  {#if closeable && !configuring}
    <button
      class="hover-target soundcard-close-button"
      tabindex="0"
      aria-label="remove card"
      on:click={deleteCard}
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
    >
      {@html closeIcon}
    </button>
  {/if}
  <div class="soundcard-ambience-layout">
    <div class="soundcard-header">
      <SoundCardTitle {card} nameChangeCallback={handleNameChangeRequest} />
      {#if attribution}
        <Attribution {attribution} isUserSound={false} />
      {/if}
    </div>
    <input
      class="soundcard-volume"
      id="volume-{card['id']}"
      type="range"
      on:change={handleVolumeChange}
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
      bind:this={volume}
      min="0"
      max="100"
    />
    <input
      bind:this={enabled}
      type="checkbox"
      class="soundcard-enabled"
      on:change={handleEnabledChange}
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
    />
  </div>
  {#if configuring === true}
    <div
      transition:fade
      class="soundcard-ambience-overlay"
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
    >
      Repeat this sound every
      <select
        name="select-from"
        class="soundcard-ambience-duration-select"
        aria-label="Select"
        required
        bind:value={fromDurationName}
        on:change={handleDurationChange}
      >
        {#each [...fromDurations] as [name, _]}
          <option value={name}>{name}</option>
        {/each}
      </select>
      to
      <select
        name="select-to"
        class="soundcard-ambience-duration-select"
        aria-label="Select"
        required
        bind:value={toDurationName}
        on:change={handleDurationChange}
      >
        {#each [...toDurations] as [name, _]}
          <option value={name}>{name}</option>
        {/each}
      </select>
      .
    </div>
  {/if}
  {#if advancedControlsEnabled}
    <!-- ambience-controls is just here so that the tutorial can refer to it, and I'd rather not use `id` -->
    <button
      class="soundcard-settings soundcard-button ambience-controls"
      class:light={configuring}
      class:dark={!configuring}
      on:click={handleClickSettings}
      on:pointerdown={stopPropagation}
      on:mousedown={stopPropagation}
    >
      {@html settingsGearIcon}
    </button>
  {/if}
</div>
